import type { PdfFieldMapping, PdfImportIds } from "./types";
import { parseDMY, parseTimeHHMM, formatLocalDate } from "./utils";
import { MILITARY_RANK_VALUES } from "../../../shared/src/enums";

/** Map full Ukrainian rank names (as printed on PDFs) to our abbreviated enum values. */
const RANK_ALIASES: Record<string, string> = {
  "старший солдат": "Ст. солдат",
  "молодший сержант": "Мл. сержант",
  "старший сержант": "Ст. сержант",
  "головний сержант": "Гл. сержант",
  "штаб сержант": "Шт. сержант",
  "штаб-сержант": "Шт. сержант",
  "старший майстер-сержант": "Ст. майстер-сержант",
  "головний майстер-сержант": "Гл. майстер-сержант",
  "молодший лейтенант": "Мл. лейтенант",
  "старший лейтенант": "Ст. лейтенант",
};

function resolveRank(raw: string): string | null {
  const trimmed = raw.trim();
  // Exact match (case-insensitive) against enum values
  const exact = MILITARY_RANK_VALUES.find((r) => r.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact;
  // Check aliases
  const alias = RANK_ALIASES[trimmed.toLowerCase()];
  if (alias) return alias;
  // Partial match — if the input contains a known rank
  for (const r of MILITARY_RANK_VALUES) {
    if (trimmed.toLowerCase().includes(r.toLowerCase())) return r;
  }
  return trimmed; // Return as-is — better to import something than nothing
}

/**
 * Maps field keys to dispatch commands and payload builders.
 * Shared across all form types — add an entry here once per logical field,
 * then reference it by key in any form definition file.
 */
export const fieldMap: Record<string, PdfFieldMapping> = {
  // ── Text fields ──────────────────────────────────────────────────────────
  patientName: {
    command: "patient.setName",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, name: v as string } : null,
  },
  rank: {
    command: "patient.setMilitaryRank",
    getPayload: (v, ids: PdfImportIds) => {
      if (!v) return null;
      const rank = resolveRank(v as string);
      return rank ? { uuid: ids.patientUuid, rank } : null;
    },
  },
  patientId: {
    command: "patient.setIdNumber",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, idNumber: v as string } : null,
  },
  dateOfBirth: {
    command: "patient.setDateOfBirth",
    getPayload: (v, ids: PdfImportIds) => {
      const ts = parseDMY(v as string);
      if (ts === null) return null;
      return { uuid: ids.patientUuid, date: formatLocalDate(ts) };
    },
  },
  caseNumber: {
    command: "case.setNumber",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, number: v as string } : null,
  },
  allergyDetails: {
    command: "patient.setAllergyDetails",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, details: v as string } : null,
  },

  // ── Case text fields ─────────────────────────────────────────────────────
  eventDate: {
    command: "case.setEventTime",
    getPayload: (v, ids: PdfImportIds) => {
      const ts = parseDMY(v as string);
      return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null;
    },
  },
  arrivalDate: {
    command: "case.setArrivalTime",
    getPayload: (v, ids: PdfImportIds) => {
      const ts = parseDMY(v as string);
      return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null;
    },
  },
  medicalFacility: {
    command: "case.setMedicalFacility",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, facility: v as string } : null,
  },
  "p1-trauma-notes": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "traumaNotes", value: v as string } : null,
  },
  notes: {
    command: "case.setNotes",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, notes: v as string } : null,
  },
  priorCareDate: {
    command: "case.setPriorCareTime",
    getPayload: (v, ids: PdfImportIds) => {
      const ts = parseDMY(v as string);
      return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null;
    },
  },
  dischargeDestination: {
    command: "case.setDischargeDestination",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, destination: v as string } : null,
  },
  medicName: {
    command: "case.setMedicName",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, name: v as string } : null,
  },
  medicFacility: {
    command: "case.setMedicFacility",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, facility: v as string } : null,
  },
  "medic-role-1": {
    command: "case.setMedicRole",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 1 } : null,
  },
  "medic-role-2": {
    command: "case.setMedicRole",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 2 } : null,
  },
  "medic-role-3": {
    command: "case.setMedicRole",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 3 } : null,
  },
  "medic-role-4": {
    command: "case.setMedicRole",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 4 } : null,
  },

  // ── R1 care text fields ──────────────────────────────────────────────────
  "p1-r1care-thoracoRightBloodMl": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "thoracoRightBloodMl", value: n } : null; },
  },
  "p1-r1care-thoracoLeftBloodMl": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "thoracoLeftBloodMl", value: n } : null; },
  },
  "r1-o2": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "o2", value: Number(v as string) } : null,
  },
  "r1-ettSize": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ettSize", value: Number(v as string) } : null,
  },
  "r1-hypothermiaDetail": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaDetail", value: v as string } : null,
  },
  "p1-r1care-anesthesiaDetail": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "anesthesiaDetail", value: v as string } : null,
  },
  "p1-r1care-tourniquetATTimes1": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetATTimes1", value: v as string } : null,
  },
  "p1-r1care-tourniquetDetail": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetDetail", value: v as string } : null,
  },

  // ── R2 case text fields ──────────────────────────────────────────────────
  "r2-additionalInfo": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "examAdditionalInfo", value: v as string } : null,
  },
  "r2-o2Flow": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "o2Flow", value: Number(v as string) } : null,
  },
  "r2-ettSize": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ettSize", value: Number(v as string) } : null,
  },
  "r2-hypothermiaDetail": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaDetail", value: v as string } : null,
  },
  "r2-anesthesiaNotes": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "anesthesiaNotes", value: v as string } : null,
  },
  "r2-dischargeDestination": {
    command: "case.setR2DischargeDestination",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, destination: v as string } : null,
  },
  "r2-medicName": {
    command: "case.setR2MedicName",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, name: v as string } : null,
  },
  "r2-medicFacility": {
    command: "case.setR2MedicFacility",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, facility: v as string } : null,
  },

  // ── Section 1.1 — Sex ────────────────────────────────────────────────────
  "sex-unknown": {
    command: "patient.setSex",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, sex: "unknown" } : null,
  },
  "sex-male": {
    command: "patient.setSex",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, sex: "male" } : null,
  },
  "sex-female": {
    command: "patient.setSex",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, sex: "female" } : null,
  },

  // ── Section 1.1 — Patient category ──────────────────────────────────────
  "category-civilian": {
    command: "patient.setCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, category: "civilian" } : null,
  },
  "category-military": {
    command: "patient.setCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, category: "military" } : null,
  },
  "category-police": {
    command: "patient.setCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, category: "police" } : null,
  },

  // ── Section 1.2 — Allergy status ─────────────────────────────────────────
  "allergy-yes": {
    command: "patient.setAllergyStatus",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, status: "yes" } : null,
  },
  "allergy-no": {
    command: "patient.setAllergyStatus",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, status: "no" } : null,
  },
  "allergy-unknown": {
    command: "patient.setAllergyStatus",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, status: "unknown" } : null,
  },

  // ── Section 1.3 — Transport type ─────────────────────────────────────────
  "transport-casevac": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "arrival", key: "casevac", value: true } : null,
  },
  "transport-mmpm": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "arrival", key: "mmpm", value: true } : null,
  },
  "transport-medevac": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "arrival", key: "medevac", value: true } : null,
  },
  "transport-other": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "arrival", key: "other", value: true } : null,
  },

  // ── Section 1.3 — Medical role ───────────────────────────────────────────
  "role-1": {
    command: "case.setRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 1 } : null,
  },
  "role-2": {
    command: "case.setRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 2 } : null,
  },
  "role-3": {
    command: "case.setRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 3 } : null,
  },
  "role-4": {
    command: "case.setRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 4 } : null,
  },

  // ── Section 1.4 — Triage category ────────────────────────────────────────
  "triage-I": {
    command: "case.setTriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "I" } : null,
  },
  "triage-II": {
    command: "case.setTriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "II" } : null,
  },
  "triage-III": {
    command: "case.setTriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "III" } : null,
  },
  "triage-IV": {
    command: "case.setTriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "IV" } : null,
  },

  // ── Section 2.6 — Prior care medications ─────────────────────────────────
  "prior-medPillPack": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "medPillPack", value: true } : null,
  },
  "prior-medTXA": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "medTXA", value: true } : null,
  },
  "prior-medAnalgesic": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "medAnalgesic", value: true } : null,
  },
  "prior-medAntibiotic": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "medAntibiotic", value: true } : null,
  },
  "p1-prior-medAnalgesicDetail": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "medAnalgesicDetail", value: v as string } : null,
  },
  "p1-prior-medAntibioticDetail": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "medAntibioticDetail", value: v as string } : null,
  },

  // ── Section 2.6 — Medication dose / route / time grid ────────────────────
  "p1-prior-medTXADose":         { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "medTXADose",         value: n } : null; } },
  "p1-prior-medTXARoute":        { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, key: "medTXARoute",        value: v as string } : null },
  "p1-prior-medTXATime":         { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "medTXATime",         value: ts } : null; } },
  "p1-prior-medAnalgesicDose":   { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "medAnalgesicDose",   value: n } : null; } },
  "p1-prior-medAnalgesicRoute":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, key: "medAnalgesicRoute",  value: v as string } : null },
  "p1-prior-medAnalgesicTime":   { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "medAnalgesicTime",   value: ts } : null; } },
  "p1-prior-medAntibioticDose":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "medAntibioticDose",  value: n } : null; } },
  "p1-prior-medAntibioticRoute": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, key: "medAntibioticRoute", value: v as string } : null },
  "p1-prior-medAntibioticTime":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "medAntibioticTime",  value: ts } : null; } },
  "p1-prior-medExtraDose":       { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "medExtraDose",       value: n } : null; } },
  "p1-prior-medExtraRoute":      { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, key: "medExtraRoute",      value: v as string } : null },
  "p1-prior-medExtraTime":       { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "medExtraTime",       value: ts } : null; } },

  // ── Section 4 — R1 care (case.setR1CareItem) ─────────────────────────────
  "r1-tourniquetRUE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetRUE", value: true } : null,
  },
  "r1-tourniquetLUE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetLUE", value: true } : null,
  },
  "r1-tourniquetRLE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetRLE", value: true } : null,
  },
  "r1-tourniquetLLE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetLLE", value: true } : null,
  },
  "r1-tourniquetAT": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetAT", value: true } : null,
  },
  "r1-tourniquetVT": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetVT", value: true } : null,
  },
  "r1-bandage": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "bandage", value: true } : null,
  },
  "r1-pressureBandage": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pressureBandage", value: true } : null,
  },
  "r1-woundCare": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "woundCare", value: true } : null,
  },
  "r1-redressing": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "redressing", value: true } : null,
  },
  "r1-packing": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "packing", value: true } : null,
  },
  "r1-occlusiveRight": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "occlusiveRight", value: true } : null,
  },
  "r1-occlusiveLeft": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "occlusiveLeft", value: true } : null,
  },
  "r1-needleDecompRight": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "needleDecompRight", value: true } : null,
  },
  "r1-needleDecompLeft": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "needleDecompLeft", value: true } : null,
  },
  "r1-sd": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "sd", value: true } : null,
  },
  "r1-nfp": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "nfp", value: true } : null,
  },
  "r1-ofp": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ofp", value: true } : null,
  },
  "r1-ngp": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ngp", value: true } : null,
  },
  "r1-ett": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ett", value: true } : null,
  },
  "r1-cricothyrotomy": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "cricothyrotomy", value: true } : null,
  },
  "r1-thoracocentesis": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracocentesis", value: true } : null,
  },
  "r1-shvl": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "shvl", value: true } : null,
  },
  "r1-thoracoRightBlood": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoRightBlood", value: true } : null,
  },
  "r1-thoracoRightAir": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoRightAir", value: true } : null,
  },
  "r1-thoracoLeftBlood": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoLeftBlood", value: true } : null,
  },
  "r1-thoracoLeftAir": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoLeftAir", value: true } : null,
  },
  "r1-pvd": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pvd", value: true } : null,
  },
  "r1-cvd": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "cvd", value: true } : null,
  },
  "r1-ioShoulder": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioShoulder", value: true } : null,
  },
  "r1-ioTibia": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioTibia", value: true } : null,
  },
  "r1-ioSternum": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioSternum", value: true } : null,
  },
  "r1-hypothermiaActive": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaActive", value: true } : null,
  },
  "r1-hypothermiaPassive": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaPassive", value: true } : null,
  },
  "r1-immobCervical": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobCervical", value: true } : null,
  },
  "r1-immobSpine": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobSpine", value: true } : null,
  },
  "r1-immobPelvis": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobPelvis", value: true } : null,
  },
  "r1-immobRUE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobRUE", value: true } : null,
  },
  "r1-immobLUE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobLUE", value: true } : null,
  },
  "r1-immobRLE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobRLE", value: true } : null,
  },
  "r1-immobLLE": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobLLE", value: true } : null,
  },
  "r1-urinaryCatheter": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "urinaryCatheter", value: true } : null,
  },
  "r1-gastricTube": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "gastricTube", value: true } : null,
  },
  "r1-reboa": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "reboa", value: true } : null,
  },
  "r1-decontamination": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "decontamination", value: true } : null,
  },
  "r1-anesthesia": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "anesthesia", value: true } : null,
  },
  "r1-anesthesiaRegional": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "anesthesiaRegional", value: true } : null,
  },
  "r1-eyeShieldRight": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "eyeShieldRight", value: true } : null,
  },
  "r1-eyeShieldLeft": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "eyeShieldLeft", value: true } : null,
  },

  // ── Section 2.3 — Prior assessment vitals ────────────────────────────────
  "p1-prior-assessRR":        { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "assessRR",        value: n } : null; } },
  "p1-prior-assessSpO2":      { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "assessSpO2",      value: n } : null; } },
  "p1-prior-assessPulse":     { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "assessPulse",     value: n } : null; } },
  "p1-prior-assessSystolic":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "assessSystolic",  value: n } : null; } },
  "p1-prior-assessDiastolic": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "assessDiastolic", value: n } : null; } },
  "p1-prior-assessAVPU":      { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, key: "assessAVPU", value: v as string } : null },
  "p1-prior-assessPain":      { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const n = Number(v); return !isNaN(n) && v !== "" ? { uuid: ids.caseUuid, key: "assessPain",      value: n } : null; } },

  // ── Section 2.4 — Prior care procedures ──────────────────────────────────
  "prior-bandage": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "bandage", value: true } : null,
  },
  "prior-packing": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "packing", value: true } : null,
  },
  "prior-oxygen": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "oxygen", value: true } : null,
  },
  "prior-ofp": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ofp", value: true } : null,
  },
  "prior-nfp": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "nfp", value: true } : null,
  },
  "prior-ngp": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ngp", value: true } : null,
  },
  "prior-ett": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ett", value: true } : null,
  },
  "prior-cricothyrotomy": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "cricothyrotomy", value: true } : null,
  },
  "prior-needleDecompression": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "needleDecompression", value: true } : null,
  },
  "prior-occlusiveDressing": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "occlusiveDressing", value: true } : null,
  },
  "prior-bvm": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "bvm", value: true } : null,
  },
  "prior-ivAccess": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ivAccess", value: true } : null,
  },
  "prior-ioAccess": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioAccess", value: true } : null,
  },
  "prior-hypothermiaPrevention": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaPrevention", value: true } : null,
  },
  "prior-immobilization": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobilization", value: true } : null,
  },
  "prior-eyeShield": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "eyeShield", value: true } : null,
  },
  "prior-other": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "other", value: true } : null,
  },
  "p1-prior-otherDetail": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "otherDetail", value: v as string } : null,
  },

  // ── Section 2.5 — Tourniquet limbs ───────────────────────────────────────
  "prior-tourniquetRUE": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetRUE", value: true } : null,
  },
  "prior-tourniquetLUE": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetLUE", value: true } : null,
  },
  "prior-tourniquetRLE": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetRLE", value: true } : null,
  },
  "prior-tourniquetLLE": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetLLE", value: true } : null,
  },
  "prior-tourniquetAT": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetAT", value: true } : null,
  },
  "prior-tourniquetVT": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetVT", value: true } : null,
  },

  // ── Section 2.5 — Tourniquet time grid ───────────────────────────────────
  "p1-prior-tourniquetRUEApp":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRUEApp",  value: ts } : null; } },
  "p1-prior-tourniquetRUERem":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRUERem",  value: ts } : null; } },
  "p1-prior-tourniquetRUEConv": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRUEConv", value: ts } : null; } },
  "p1-prior-tourniquetRUERepo": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRUERepo", value: ts } : null; } },
  "p1-prior-tourniquetLUEApp":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLUEApp",  value: ts } : null; } },
  "p1-prior-tourniquetLUERem":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLUERem",  value: ts } : null; } },
  "p1-prior-tourniquetLUEConv": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLUEConv", value: ts } : null; } },
  "p1-prior-tourniquetLUERepo": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLUERepo", value: ts } : null; } },
  "p1-prior-tourniquetRLEApp":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRLEApp",  value: ts } : null; } },
  "p1-prior-tourniquetRLERem":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRLERem",  value: ts } : null; } },
  "p1-prior-tourniquetRLEConv": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRLEConv", value: ts } : null; } },
  "p1-prior-tourniquetRLERepo": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetRLERepo", value: ts } : null; } },
  "p1-prior-tourniquetLLEApp":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLLEApp",  value: ts } : null; } },
  "p1-prior-tourniquetLLERem":  { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLLERem",  value: ts } : null; } },
  "p1-prior-tourniquetLLEConv": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLLEConv", value: ts } : null; } },
  "p1-prior-tourniquetLLERepo": { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetLLERepo", value: ts } : null; } },
  "p1-prior-tourniquetATApp":    { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetATApp",   value: ts } : null; } },
  "p1-prior-tourniquetATRem":    { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => { const ts = parseTimeHHMM(v as string); return ts !== null ? { uuid: ids.caseUuid, key: "tourniquetATRem",   value: ts } : null; } },
  "p1-prior-tourniquetDetail":   { command: "case.setPriorCareItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, key: "tourniquetDetail", value: v as string } : null },

  // ── Section 2.2 — Prior care provider ────────────────────────────────────
  "prior-provider-self": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "provider", value: "self" } : null,
  },
  "prior-provider-nonpro": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "provider", value: "nonPro" } : null,
  },
  "prior-provider-medpro": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "provider", value: "medPro" } : null,
  },
  "prior-provider-unknown": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "provider", value: "unknown" } : null,
  },
  "prior-provider-none": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "provider", value: "none" } : null,
  },

  // ── Section 3.1 — Trauma type ─────────────────────────────────────────────
  "p1-trauma-gunshot": {
    command: "case.setTraumaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "gunshot", value: true } : null,
  },
  "p1-trauma-explosive": {
    command: "case.setTraumaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "explosive", value: true } : null,
  },
  "p1-trauma-blunt": {
    command: "case.setTraumaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "blunt", value: true } : null,
  },
  "p1-trauma-open": {
    command: "case.setTraumaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "open", value: true } : null,
  },
  "p1-trauma-crush": {
    command: "case.setTraumaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "crush", value: true } : null,
  },
  "p1-prior-assessWarm": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "assessWarm", value: true } : null,
  },
  "p1-prior-assessCold": {
    command: "case.setPriorCareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "assessCold", value: true } : null,
  },
  "p1-trauma-penetrating": {
    command: "case.setTraumaPenetrating",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, penetrating: true } : null,
  },
  "p1-trauma-nonpenetrating": {
    command: "case.setTraumaNonPenetrating",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, value: true } : null,
  },

  // ── Section 3.2 — Condition type ─────────────────────────────────────────
  "p1-condition-somatic": {
    command: "case.setConditionTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "somatic", value: true } : null,
  },
  "p1-condition-infectious": {
    command: "case.setConditionTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "infectious", value: true } : null,
  },
  "p1-condition-psychiatric": {
    command: "case.setConditionTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "psychiatric", value: true } : null,
  },
  "p1-condition-poisoning": {
    command: "case.setConditionTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "poisoning", value: true } : null,
  },

  // ── Section 3.3 — CBRN ───────────────────────────────────────────────────
  "p1-cbrn-chemical": {
    command: "case.setCbrnItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "chemical", value: true } : null,
  },
  "p1-cbrn-biological": {
    command: "case.setCbrnItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "biological", value: true } : null,
  },
  "p1-cbrn-radiological": {
    command: "case.setCbrnItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "radiological", value: true } : null,
  },
  "p1-cbrn-nuclear": {
    command: "case.setCbrnItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "nuclear", value: true } : null,
  },

  // ── Section 10.2 — Tetanus ────────────────────────────────────────────────
  "r1-tetanusAdministered": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tetanusAdministered", value: true } : null,
  },
  "p1-tetanusProphylaxis": {
    command: "case.setTetanusProphylaxis",
    getPayload: (v, ids: PdfImportIds) =>
      typeof v === "string" && v ? { uuid: ids.caseUuid, prophylaxis: v } : null,
  },
  "r1-care-bloodTransfused": {
    command: "case.setR1CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "bloodTransfused", value: true } : null,
  },

  // ── Section 10.3 — Blood type unknown ────────────────────────────────────
  "blood-type-unknown": {
    command: "patient.setBloodType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, bloodType: "unknown" } : null,
  },

  // ── Section 8 — Death ─────────────────────────────────────────────────────
  "p1-deathDate": {
    command: "case.setDeathTime",
    getPayload: (v, ids: PdfImportIds) => {
      const ts = parseDMY(v as string);
      return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null;
    },
  },
  "p1-death-circumstances": {
    command: "case.setDeathCircumstances",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, circumstances: v as string } : null,
  },

  // ── Section 10.3 — Blood type text (AB0 / Rh) ────────────────────────────
  "p1-blood-type": {
    command: "patient.setBloodType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, bloodType: v as string } : null,
  },
  "p1-rh-factor": {
    command: "patient.setRhFactor",
    getPayload: (v, ids: PdfImportIds) => {
      if (!v) return null;
      const s = String(v).trim();
      const rh = s === "+" ? "positive" : s === "-" ? "negative" : s;
      return { uuid: ids.patientUuid, rhFactor: rh };
    },
  },

  // ── Section 12 — Diagnosis ────────────────────────────────────────────────
  "p1-diagnosis": {
    command: "case.setDiagnosis",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, diagnosis: v as string } : null,
  },

  // ── Section 13 — Discharge time/date ─────────────────────────────────────
  "p1-dischargeDate": {
    command: "case.setDischargeTime",
    getPayload: (v, ids: PdfImportIds) => {
      const ts = parseDMY(v as string);
      return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null;
    },
  },

  // ── Kit 1 / Kit 2 ─────────────────────────────────────────────────────────
  "r1-kit1": {
    command: "case.setR1Kit1",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, kit: "1" } : null,
  },
  "r1-kit2": {
    command: "case.setR1Kit2",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, kit: "1" } : null,
  },

  // ── Section 13 — Evacuation category ─────────────────────────────────────
  "evac-cat-I": {
    command: "case.setEvacuationCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, evacuationCategory: "I" } : null,
  },
  "evac-cat-II": {
    command: "case.setEvacuationCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, evacuationCategory: "II" } : null,
  },
  "evac-cat-III": {
    command: "case.setEvacuationCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, evacuationCategory: "III" } : null,
  },

  // ── Section 13 — Discharge transport type ────────────────────────────────
  "discharge-transport-casevac": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "discharge", key: "casevac", value: true } : null,
  },
  "discharge-transport-mmpm": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "discharge", key: "mmpm", value: true } : null,
  },
  "discharge-transport-medevac": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "discharge", key: "medevac", value: true } : null,
  },

  // ── Section 13 — Vehicle type ─────────────────────────────────────────────
  "discharge-vehicle-A": {
    command: "case.setDischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "A" } : null,
  },
  "discharge-vehicle-B": {
    command: "case.setDischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "B" } : null,
  },
  "discharge-vehicle-C": {
    command: "case.setDischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "C" } : null,
  },
  "discharge-vehicle-helicopter": {
    command: "case.setDischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "helicopter" } : null,
  },
  "discharge-vehicle-boat": {
    command: "case.setDischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "boat" } : null,
  },
  "discharge-vehicle-train": {
    command: "case.setDischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "train" } : null,
  },

  // ── Section 13 — Discharge position ──────────────────────────────────────
  "discharge-position-sitting": {
    command: "case.setDischargePosition",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, position: "sitting" } : null,
  },
  "discharge-position-lying": {
    command: "case.setDischargePosition",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, position: "lying" } : null,
  },

  // ── Section 13 — Escort ───────────────────────────────────────────────────
  "discharge-escort-doctor": {
    command: "case.setEscortDoctor",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, escortDoctor: true } : null,
  },
  "discharge-escort-nurse": {
    command: "case.setEscortNurse",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, escortNurse: true } : null,
  },

  // ── Section 13 — Discharge equipment ─────────────────────────────────────
  "discharge-equip-oxygen": {
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "oxygen", value: true } : null,
  },
  "discharge-equip-ventilator": {
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ventilator", value: true } : null,
  },
  "discharge-equip-other": {
    command: "case.setDischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "other", value: true } : null,
  },

  // ── Section 13 — Discharge role ───────────────────────────────────────────
  "discharge-role-1": {
    command: "case.setDischargeRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 1 } : null,
  },
  "discharge-role-2": {
    command: "case.setDischargeRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 2 } : null,
  },
  "discharge-role-3": {
    command: "case.setDischargeRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 3 } : null,
  },
  "discharge-role-4": {
    command: "case.setDischargeRole",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, role: 4 } : null,
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PAGE 2
  // ══════════════════════════════════════════════════════════════════════════

  // ── Section 15 — R2 triage category ─────────────────────────────────────
  "r2-triage-I": {
    command: "case.setR2TriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "I" } : null,
  },
  "r2-triage-II": {
    command: "case.setR2TriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "II" } : null,
  },
  "r2-triage-III": {
    command: "case.setR2TriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "III" } : null,
  },
  "r2-triage-IV": {
    command: "case.setR2TriageCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, triageCategory: "IV" } : null,
  },

  // ── Section 18.1 — R2 tourniquet limbs ───────────────────────────────────
  "r2-tourniquetRUE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetRUE", value: true } : null,
  },
  "r2-tourniquetLUE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetLUE", value: true } : null,
  },
  "r2-tourniquetRLE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetRLE", value: true } : null,
  },
  "r2-tourniquetLLE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tourniquetLLE", value: true } : null,
  },

  // ── Section 18.2 — R2 airway ─────────────────────────────────────────────
  "r2-o2": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "o2", value: true } : null,
  },
  "r2-nfp": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "nfp", value: true } : null,
  },
  "r2-ofp": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ofp", value: true } : null,
  },
  "r2-shvl": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "shvl", value: true } : null,
  },
  "r2-ngp": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ngp", value: true } : null,
  },
  "r2-ett": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ett", value: true } : null,
  },
  "r2-cricothyrotomy": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "cricothyrotomy", value: true } : null,
  },

  // ── Section 18.3 — R2 occlusive / needle decompression ───────────────────
  "r2-occlusiveRight": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "occlusiveRight", value: true } : null,
  },
  "r2-occlusiveLeft": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "occlusiveLeft", value: true } : null,
  },
  "r2-needleDecompRight": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "needleDecompRight", value: true } : null,
  },
  "r2-needleDecompLeft": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "needleDecompLeft", value: true } : null,
  },

  // ── Section 18.4 — R2 access ─────────────────────────────────────────────
  "r2-pvd": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pvd", value: true } : null,
  },
  "r2-cvd": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "cvd", value: true } : null,
  },
  "r2-arterial": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "arterial", value: true } : null,
  },
  "r2-ioShoulder": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioShoulder", value: true } : null,
  },
  "r2-ioTibia": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioTibia", value: true } : null,
  },
  "r2-ioSternum": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ioSternum", value: true } : null,
  },

  // ── Section 18.5 — R2 hypothermia prevention ─────────────────────────────
  "r2-hypothermiaActive": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaActive", value: true } : null,
  },
  "r2-hypothermiaPassive": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "hypothermiaPassive", value: true } : null,
  },

  // ── Section 18.6 — R2 immobilization ─────────────────────────────────────
  "r2-immobCervical": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobCervical", value: true } : null,
  },
  "r2-immobSpine": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobSpine", value: true } : null,
  },
  "r2-immobPelvis": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "immobPelvis", value: true } : null,
  },
  "r2-immobRUE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pvdRue", value: true } : null,
  },
  "r2-immobLUE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pvdLue", value: true } : null,
  },
  "r2-immobRLE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pvdRle", value: true } : null,
  },
  "r2-immobLLE": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "pvdLle", value: true } : null,
  },

  // ── Section 18.7 — R2 wound care ─────────────────────────────────────────
  "r2-bandage": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "bandage", value: true } : null,
  },
  "r2-packing": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "packing", value: true } : null,
  },

  // ── Section 18.8 — R2 urinary catheter / gastric tube / eye shield ────────
  "r2-urinaryCatheter": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "urinaryCatheter", value: true } : null,
  },
  "r2-gastricTube": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "gastricTube", value: true } : null,
  },
  "r2-eyeShieldRight": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "eyeShieldRight", value: true } : null,
  },
  "r2-eyeShieldLeft": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "eyeShieldLeft", value: true } : null,
  },

  // ── Section 18.9 — R2 decontamination ────────────────────────────────────
  "r2-decontamination": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "decontamination", value: true } : null,
  },

  // ── Section 18.10 — Surgery types (need ids.surgeryUuid) ────────────────
  "surg-craniectomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "craniectomy", value: true } : null,
  },
  "surg-cantholysis": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "cantholysis", value: true } : null,
  },
  "surg-tracheostomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "tracheostomy", value: true } : null,
  },
  "surg-thoracotomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "thoracotomy", value: true } : null,
  },
  "surg-pericardiotomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "pericardiotomy", value: true } : null,
  },
  "surg-thoracocentesis": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "thoracocentesis", value: true } : null,
  },
  "r2care-thoracoRightBlood": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoRightBlood", value: true } : null,
  },
  "r2care-thoracoRightAir": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoRightAir", value: true } : null,
  },
  "r2care-thoracoLeftBlood": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoLeftBlood", value: true } : null,
  },
  "r2care-thoracoLeftAir": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "thoracoLeftAir", value: true } : null,
  },
  "surg-laparotomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "laparotomy", value: true } : null,
  },
  "surg-azf": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "azf", value: true } : null,
  },
  "surg-amputation": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "amputation", value: true } : null,
  },
  "surg-fasciotomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "fasciotomy", value: true } : null,
  },
  "surg-escharotomy": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "escharotomy", value: true } : null,
  },
  "surg-vascularShunt": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "vascularShunt", value: true } : null,
  },

  // ── Section 18.11 — REBOA ─────────────────────────────────────────────────
  "surg-reboa": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "reboa", value: true } : null,
  },

  // ── Section 18.12 — Wound closure / debridement ───────────────────────────
  "surg-suturing": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "suturing", value: true } : null,
  },
  "surg-debridement": {
    command: "surgery.setSurgeryTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "debridement", value: true } : null,
  },

  // ── Section 18.13 — Surgery description ──────────────────────────────────
  // ── Section 18.13 — Surgery description ──────────────────────────────────
  "surg-description": {
    command: "surgery.setDescription",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, description: v as string } : null,
  },

  // ── Section 18.14 — Anesthesia type ──────────────────────────────────────
  "surg-anesthesia-local": {
    command: "surgery.setAnesthesiaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "local", value: true } : null,
  },
  "surg-anesthesia-regional": {
    command: "surgery.setAnesthesiaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "regional", value: true } : null,
  },
  "surg-anesthesia-general": {
    command: "surgery.setAnesthesiaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "general", value: true } : null,
  },
  "surg-anesthesia-sedation": {
    command: "surgery.setAnesthesiaTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v && ids.surgeryUuid ? { uuid: ids.surgeryUuid, key: "sedation", value: true } : null,
  },

  // ── Section 23.1 — R2 tetanus prophylaxis ────────────────────────────────
  "r2-tetanusAdministered": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "tetanusAdministered", value: true } : null,
  },

  // ── Section 23.1 — R2 blood transfused ───────────────────────────────────
  "r2care-bloodTransfused": {
    command: "case.setR2CareItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "bloodTransfused", value: true } : null,
  },

  // ── Section 23.2 — Blood type text / Rh factor (p2) ──────────────────────
  "p2-blood-type": {
    command: "patient.setBloodType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, bloodType: v as string } : null,
  },
  "p2-rh-factor": {
    command: "patient.setRhFactor",
    getPayload: (v, ids: PdfImportIds) => {
      if (!v) return null;
      const s = String(v).trim();
      const rh = s === "+" ? "positive" : s === "-" ? "negative" : s;
      return { uuid: ids.patientUuid, rhFactor: rh };
    },
  },

  // ── Section 23.2 — Blood type unknown (p2) ───────────────────────────────
  "p2-blood-type-unknown": {
    command: "patient.setBloodType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.patientUuid, bloodType: "unknown" } : null,
  },

  // ── Kit 1 / Kit 2 (R2) ───────────────────────────────────────────────────
  "r2-kit1": {
    command: "case.setR2Kit1",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, kit: "1" } : null,
  },
  "r2-kit2": {
    command: "case.setR2Kit2",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, kit: "2" } : null,
  },

  // ── Section 26 — R2 evacuation category ──────────────────────────────────
  "r2-evac-cat-I": {
    command: "case.setR2EvacuationCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, evacuationCategory: "I" } : null,
  },
  "r2-evac-cat-II": {
    command: "case.setR2EvacuationCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, evacuationCategory: "II" } : null,
  },
  "r2-evac-cat-III": {
    command: "case.setR2EvacuationCategory",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, evacuationCategory: "III" } : null,
  },

  // ── Section 26 — R2 discharge transport type ──────────────────────────────
  "r2-discharge-transport-casevac": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "r2Discharge", key: "casevac", value: true } : null,
  },
  "r2-discharge-transport-mmpm": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "r2Discharge", key: "mmpm", value: true } : null,
  },
  "r2-discharge-transport-medevac": {
    command: "case.setTransportTypeItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, field: "r2Discharge", key: "medevac", value: true } : null,
  },

  // ── Section 26 — R2 vehicle type ─────────────────────────────────────────
  "r2-discharge-vehicle-A": {
    command: "case.setR2DischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "A" } : null,
  },
  "r2-discharge-vehicle-B": {
    command: "case.setR2DischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "B" } : null,
  },
  "r2-discharge-vehicle-C": {
    command: "case.setR2DischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "C" } : null,
  },
  "r2-discharge-vehicle-helicopter": {
    command: "case.setR2DischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "helicopter" } : null,
  },
  "r2-discharge-vehicle-boat": {
    command: "case.setR2DischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "boat" } : null,
  },
  "r2-discharge-vehicle-train": {
    command: "case.setR2DischargeVehicleType",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, vehicleType: "train" } : null,
  },

  // ── Section 26 — R2 discharge position ───────────────────────────────────
  "r2-discharge-position-sitting": {
    command: "case.setR2DischargePosition",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, position: "sitting" } : null,
  },
  "r2-discharge-position-lying": {
    command: "case.setR2DischargePosition",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, position: "lying" } : null,
  },

  // ── Section 26 — R2 escort ────────────────────────────────────────────────
  "r2-discharge-escort-doctor": {
    command: "case.setR2EscortDoctor",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, escortDoctor: true } : null,
  },
  "r2-discharge-escort-nurse": {
    command: "case.setR2EscortNurse",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, escortNurse: true } : null,
  },

  // ── Section 26 — R2 discharge equipment ──────────────────────────────────
  "r2-discharge-equip-oxygen": {
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "oxygen", value: true } : null,
  },
  "r2-discharge-equip-ventilator": {
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "ventilator", value: true } : null,
  },
  "r2-discharge-equip-other": {
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "other", value: true } : null,
  },
  "p2-discharge-vehicle-id": {
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "vehicleId", value: v as string } : null,
  },
  "p2-discharge-other-detail": {
    command: "case.setR2DischargeEquipmentItem",
    getPayload: (v, ids: PdfImportIds) =>
      v ? { uuid: ids.caseUuid, key: "otherDetail", value: v as string } : null,
  },

  // ── Section 26/27 — R2 discharge role + medic role ───────────────────────
  "r2-discharge-role-1": { command: "case.setR2DischargeRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 1 } : null },
  "r2-discharge-role-2": { command: "case.setR2DischargeRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 2 } : null },
  "r2-discharge-role-3": { command: "case.setR2DischargeRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 3 } : null },
  "r2-discharge-role-4": { command: "case.setR2DischargeRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 4 } : null },
  "r2-medic-role-1": { command: "case.setR2MedicRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 1 } : null },
  "r2-medic-role-2": { command: "case.setR2MedicRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 2 } : null },
  "r2-medic-role-3": { command: "case.setR2MedicRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 3 } : null },
  "r2-medic-role-4": { command: "case.setR2MedicRole", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, role: 4 } : null },

  // ── Page 2 — arrival / death / discharge dates ────────────────────────────
  "r2-arrivalDate": {
    command: "case.setR2ArrivalTime",
    getPayload: (v, ids: PdfImportIds) => { const ts = parseDMY(v as string); return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null; },
  },
  "r2-deathDate": {
    command: "case.setR2DeathTime",
    getPayload: (v, ids: PdfImportIds) => { const ts = parseDMY(v as string); return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null; },
  },
  "r2-death-circumstances": {
    command: "case.setR2DeathCircumstances",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, circumstances: v as string } : null,
  },
  "r2-dischargeDate": {
    command: "case.setR2DischargeTime",
    getPayload: (v, ids: PdfImportIds) => { const ts = parseDMY(v as string); return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null; },
  },
  "r2-surgStartDate": {
    command: "case.setR2SurgStartTime",
    getPayload: (v, ids: PdfImportIds) => { const ts = parseDMY(v as string); return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null; },
  },
  "r2-surgEndDate": {
    command: "case.setR2SurgEndTime",
    getPayload: (v, ids: PdfImportIds) => { const ts = parseDMY(v as string); return ts !== null ? { uuid: ids.caseUuid, timestamp: ts } : null; },
  },

  // ── Page 2 — diagnosis / notes ────────────────────────────────────────────
  "r2-diagnosis": {
    command: "case.setR2Diagnosis",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, diagnosis: v as string } : null,
  },
  "r2-notes": {
    command: "case.setR2Notes",
    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, notes: v as string } : null,
  },

  // ── Page 2 — prior care on role 1 ────────────────────────────────────────
  "r2-prior-care-given":   { command: "case.setR2PriorCare", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, status: "given"   } : null },
  "r2-prior-care-unknown": { command: "case.setR2PriorCare", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, status: "unknown" } : null },
  "r2-prior-care-none":    { command: "case.setR2PriorCare", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.caseUuid, status: "none"    } : null },

  // ── Section 16 — R2 patient examination (Огляд пацієнта) ─────────────────
  // All entries use ids.examUuid (exam entity created in importForm001Fields)
  // Airway status
  "r2-exam-airway-patent":           { command: "exam.setAirwayStatus",      getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, airwayStatus: "patent" } : null },
  "r2-exam-airway-obstruct-partial": { command: "exam.setAirwayStatus",      getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, airwayStatus: "partialObstruction" } : null },
  // Advanced airway device
  "p2-exam-airway-present": { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "advancedAirwayPresent", value: true } : null },
  "r2-exam-airway-ett":     { command: "exam.setAdvancedAirwayType",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "ETT" } : null },
  "r2-exam-airway-lma":     { command: "exam.setAdvancedAirwayType",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "LMA" } : null },
  "p2-airway-device-NPA":   { command: "exam.setAdvancedAirwayType",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "NPA" } : null },
  "r2-exam-airway-type":    { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "advancedAirwayTypeDetail", value: v as string } : null },
  // Respiratory support
  "r2-exam-resp-support":      { command: "exam.setBreathingSoundsItem",   getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "respiratorySupport", value: true } : null },
  "r2-exam-resp-support-type": { command: "exam.setRespiratorySupportType", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: v as string } : null },
  // Breath sounds (Дихальні шуми)
  "r2-exam-sounds-vb-r":     { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "vesicularR",  value: true } : null },
  "r2-exam-sounds-vb-l":     { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "vesicularL",  value: true } : null },
  "r2-exam-sounds-wet-r":    { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "wetRalesR",   value: true } : null },
  "r2-exam-sounds-wet-l":    { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "wetRalesL",   value: true } : null },
  "r2-exam-sounds-dry-r":    { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "dryRalesR",   value: true } : null },
  "r2-exam-sounds-dry-l":    { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "dryRalesL",   value: true } : null },
  "r2-exam-sounds-weak-r":   { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "diminishedR", value: true } : null },
  "r2-exam-sounds-weak-l":   { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "diminishedL", value: true } : null },
  "r2-exam-sounds-absent-r": { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "absentR",     value: true } : null },
  "r2-exam-sounds-absent-l": { command: "exam.setBreathingSoundsItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "absentL",     value: true } : null },
  // Skin (Шкіра)
  "r2-exam-skin-warm":     { command: "exam.setSkinTemperature", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "warm"     } : null },
  "r2-exam-skin-cold":     { command: "exam.setSkinTemperature", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "cold"     } : null },
  "r2-exam-skin-pink":     { command: "exam.setSkinPink",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  "r2-exam-skin-pale":     { command: "exam.setSkinPale",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  "r2-exam-skin-cyanotic": { command: "exam.setSkinCyanotic",    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  "r2-exam-skin-dry":      { command: "exam.setSkinDry",         getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  "r2-exam-skin-moist":    { command: "exam.setSkinMoist",       getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  "r2-exam-skin-rash":     { command: "exam.setSkinRash",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  "r2-exam-skin-edema":    { command: "exam.setSkinEdema",       getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true      } : null },
  // Peripheral pulse
  "r2-exam-pulse-present": { command: "exam.setPeripheralPulse", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "present" } : null },
  "r2-exam-pulse-weak":    { command: "exam.setPeripheralPulse", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "weak"    } : null },
  "r2-exam-pulse-absent":  { command: "exam.setPeripheralPulse", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "absent"  } : null },
  // Capillary refill (fast = <2s = false, slow = >2s = true)
  "r2-exam-caprefill-fast": { command: "exam.setCapillaryRefillOver2s", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-caprefill-slow": { command: "exam.setCapillaryRefillOver2s", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // Heart sounds (clear = true, muffled = false)
  "r2-exam-heart-clear":   { command: "exam.setHeartSoundsClear", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-heart-muffled": { command: "exam.setHeartSoundsClear", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  // Skull fracture
  "r2-exam-skull-no":  { command: "exam.setSkullFracture", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-skull-yes": { command: "exam.setSkullFracture", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // CSF leak
  "r2-exam-csf-no":  { command: "exam.setCsfLeak", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-csf-yes": { command: "exam.setCsfLeak", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // Cervical spine injury
  "r2-exam-spine-no":  { command: "exam.setCervicalSpineInjury", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-spine-yes": { command: "exam.setCervicalSpineInjury", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // Trachea deviation
  "r2-exam-trachea-r": { command: "exam.setTracheaDeviationRight", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true } : null },
  "r2-exam-trachea-l": { command: "exam.setTracheaDeviationLeft",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true } : null },
  // Jugular veins distended / collapsed
  "r2-exam-jvd-no":  { command: "exam.setJugularVeinsDistended", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-jvd-yes": { command: "exam.setJugularVeinsDistended", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-jvc-no":  { command: "exam.setJugularVeinsCollapsed",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-jvc-yes": { command: "exam.setJugularVeinsCollapsed",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // Chest
  "r2-exam-chest-symmetric":       { command: "exam.setChestSymmetric",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: "1"   } : null },
  "r2-exam-chest-asymm-r":         { command: "exam.setChestAsymRight",         getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-chest-asymm-l":         { command: "exam.setChestAsymLeft",          getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-chest-flail-r":         { command: "exam.setChestFlailRight",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-chest-flail-l":         { command: "exam.setChestFlailLeft",         getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-chest-penetrating-no":  { command: "exam.setChestPenetratingRight",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-chest-penetrating-yes": { command: "exam.setChestPenetratingRight",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // Abdomen
  "r2-exam-abdomen-normal":      { command: "exam.setAbdomenDistended",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-abdomen-distended":   { command: "exam.setAbdomenDistended",        getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-palp-nontender":      { command: "exam.setAbdomenPainful",          getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-palp-tender":         { command: "exam.setAbdomenPainful",          getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-peritonism-no":       { command: "exam.setPeritonealIrritation",    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-peritonism-yes":      { command: "exam.setPeritonealIrritation",    getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-abd-penetrating-no":  { command: "exam.setAbdomenPenetratingTrauma", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-abd-penetrating-yes": { command: "exam.setAbdomenPenetratingTrauma", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  // Pelvis
  "r2-exam-pelvis-stable-yes": { command: "exam.setPelvisStable", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-pelvis-stable-no":  { command: "exam.setPelvisStable", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  // Limb deformation
  "r2-exam-limb-deformity-no":  { command: "exam.setDeformationAny",      getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-limb-deformity-yes": { command: "exam.setDeformationAny",      getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-limb-rue":           { command: "exam.setLimbDeformationItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "rue", value: true } : null },
  "r2-exam-limb-lue":           { command: "exam.setLimbDeformationItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "lue", value: true } : null },
  "r2-exam-limb-rle":           { command: "exam.setLimbDeformationItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "rle", value: true } : null },
  "r2-exam-limb-lle":           { command: "exam.setLimbDeformationItem", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "lle", value: true } : null },
  // Neurological deficit
  "r2-exam-neuro-deficit-no":  { command: "exam.setNeuroDeficitAny",   getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-neuro-deficit-yes": { command: "exam.setNeuroDeficitAny",   getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-neuro-rue":         { command: "exam.setNeuroDeficitItem",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "rue", value: true } : null },
  "r2-exam-neuro-lue":         { command: "exam.setNeuroDeficitItem",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "lue", value: true } : null },
  "r2-exam-neuro-rle":         { command: "exam.setNeuroDeficitItem",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "rle", value: true } : null },
  "r2-exam-neuro-lle":         { command: "exam.setNeuroDeficitItem",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, key: "lle", value: true } : null },
  // GCS
  "r2-exam-gcs": {
    command: "exam.setGcsTotal",
    getPayload: (v, ids: PdfImportIds) => {
      const n = Number(v);
      return v && !isNaN(n) ? { uuid: ids.examUuid!, value: n } : null;
    },
  },
  // Pupils equal: text field, value is <, =, >, or -
  "r2-exam-pupils-equal": { command: "exam.setPupilsEqual", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: v as string } : null },
  // Light reaction: S = sinister = left, D = dexter = right
  "r2-exam-light-yes-s": { command: "exam.setPupilsLightReactionLeft",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-light-yes-d": { command: "exam.setPupilsLightReactionRight", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: true  } : null },
  "r2-exam-light-no-s":  { command: "exam.setPupilsLightReactionLeft",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  "r2-exam-light-no-d":  { command: "exam.setPupilsLightReactionRight", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: false } : null },
  // Vision
  "r2-exam-vision-r": { command: "exam.setVisionRight", getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: v as string } : null },
  "r2-exam-vision-l": { command: "exam.setVisionLeft",  getPayload: (v, ids: PdfImportIds) => v ? { uuid: ids.examUuid!, value: v as string } : null },
};
